import React, { useEffect, useState } from 'react';
import { Link } from 'react-router-dom';
import { useLanguage } from '../context/LanguageContext';
import { useCart } from '../context/CartContext';
import { Button } from '../components/ui/button';
import { Card, CardContent } from '../components/ui/card';
import { ShoppingCart, ExternalLink, Music, Loader2, Disc, Package } from 'lucide-react';
import axios from 'axios';
import { toast } from 'sonner';

const BACKEND_URL = process.env.REACT_APP_BACKEND_URL;
const API = `${BACKEND_URL}/api`;

export default function MusicPage() {
  const { t, language } = useLanguage();
  const { addItem } = useCart();
  const [allItems, setAllItems] = useState([]);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState(false);

  useEffect(() => {
    let cancelled = false;
    const fetchAlbums = async () => {
      try {
        const response = await axios.get(`${API}/albums`);
        if (!cancelled) {
          setAllItems(response.data);
          setError(false);
        }
      } catch (err) {
        console.error('Error fetching albums:', err);
        if (!cancelled) setError(true);
      } finally {
        if (!cancelled) setLoading(false);
      }
    };
    fetchAlbums();
    return () => { cancelled = true; };
  }, []);

  // Music page: albums + singles + collections (everything except soundtracks)
  const albums = allItems.filter(item => item.item_type === 'album');
  const singles = allItems.filter(item => item.item_type === 'single');
  const collections = allItems.filter(item => item.item_type === 'collection');

  const handleAdd = (item) => {
    addItem(item, 'digital', 1);
    toast.success(
      t(`Added to cart: ${item.title_es || item.title}`, `Añadido al carrito: ${item.title_es || item.title}`)
    );
  };

  const MusicCard = ({ item }) => (
    <Card 
      className="overflow-hidden border-0 shadow-lg hover:shadow-xl transition-shadow duration-300 bg-white"
      data-testid={`music-item-${item.id}`}
    >
      <Link to={`/music/${item.id}`} className="block">
        <div className="relative aspect-square overflow-hidden group">
          <img 
            src={item.image_url} 
            alt={language === 'en' ? item.title : item.title_es}
            className="w-full h-full object-cover group-hover:scale-105 transition-transform duration-500"
          />
          <div className="absolute inset-0 bg-charcoal/0 group-hover:bg-charcoal/40 transition-colors duration-300 flex items-center justify-center">
            <span className="font-lato text-white text-sm uppercase tracking-wider opacity-0 group-hover:opacity-100 transition-opacity duration-300 bg-gold-500 px-4 py-2 rounded-full">
              {t('View Details', 'Ver Detalles')}
            </span>
          </div>
          {item.featured && (
            <span className="absolute top-4 left-4 bg-gold-500 text-white font-lato text-xs uppercase tracking-wider px-3 py-1">
              {t('Featured', 'Destacado')}
            </span>
          )}
        </div>
      </Link>
      <CardContent className="p-6">
        <Link to={`/music/${item.id}`} className="block hover:text-gold-600 transition-colors">
          <div className="flex items-start justify-between mb-2">
            <div>
              <h3 className="font-playfair text-xl font-bold text-charcoal hover:text-gold-600 transition-colors">
                {language === 'en' ? item.title : item.title_es}
              </h3>
              <p className="font-lato text-gray-500 text-sm">{item.year}</p>
            </div>
            <span className="font-playfair text-2xl font-bold text-gold-600">
              ${item.price.toFixed(2)}
            </span>
          </div>
        </Link>
        <p className="font-lato text-gray-600 text-sm mb-4 line-clamp-2">
          {language === 'en' ? item.description : item.description_es}
        </p>
        {item.songs && item.songs.length > 0 && item.item_type === 'album' && (
          <div className="mb-4">
            <p className="font-lato text-xs text-gray-500 uppercase tracking-wider mb-2">
              {t('Includes', 'Incluye')}:
            </p>
            <p className="font-lato text-sm text-gray-600">
              {item.songs.slice(0, 3).join(', ')}
              {item.songs.length > 3 && ` +${item.songs.length - 3} ${t('more', 'más')}`}
            </p>
          </div>
        )}
        <div className="flex gap-3">
          <Button 
            onClick={(e) => {
              e.preventDefault();
              handleAdd(item);
            }}
            className="flex-1 bg-gold-500 hover:bg-gold-600 text-white font-lato uppercase tracking-wider"
            data-testid={`buy-${item.id}`}
          >
            <ShoppingCart className="w-4 h-4 mr-2" />
            {t('Add to Cart', 'Añadir al Carrito')}
          </Button>
          {item.spotify_url && (
            <a 
              href={item.spotify_url}
              target="_blank"
              rel="noopener noreferrer"
            >
              <Button 
                variant="outline" 
                className="border-gold-300 text-gold-600 hover:bg-gold-50"
              >
                <ExternalLink className="w-4 h-4" />
              </Button>
            </a>
          )}
        </div>
      </CardContent>
    </Card>
  );

  return (
    <div className="min-h-screen bg-white" data-testid="music-page">
      {/* Hero */}
      <section className="pt-32 pb-16 bg-charcoal">
        <div className="container mx-auto px-4 md:px-8">
          <p className="font-lato text-gold-400 text-sm uppercase tracking-[0.3em] mb-2">
            {t('Sarah Capeles', 'Sarah Capeles')}
          </p>
          <h1 className="font-playfair text-5xl md:text-6xl font-bold text-white mb-4">
            {t('Music Store', 'Tienda de Música')}
          </h1>
          <p className="font-lato text-xl text-gray-400 max-w-2xl">
            {t(
              'Albums and singles from Sarah Capeles. Support her ministry by purchasing her music.',
              'Álbumes y sencillos de Sarah Capeles. Apoya su ministerio comprando su música.'
            )}
          </p>
        </div>
      </section>

      {loading ? (
        <div className="flex items-center justify-center py-20">
          <Loader2 className="w-8 h-8 text-gold-500 animate-spin" />
        </div>
      ) : error ? (
        <div className="flex flex-col items-center justify-center py-20 gap-4">
          <p className="font-lato text-gray-500 text-lg">
            {t('Failed to load music', 'Error al cargar la música')}
          </p>
          <Button 
            onClick={() => { setLoading(true); setError(false); window.location.reload(); }}
            className="bg-gold-500 hover:bg-gold-400 text-white font-lato"
            data-testid="retry-button"
          >
            {t('Try Again', 'Intentar de Nuevo')}
          </Button>
        </div>
      ) : (
        <>
          {/* Albums Section */}
          <section className="py-16" data-testid="albums-section">
            <div className="container mx-auto px-4 md:px-8">
              <div className="flex items-center gap-4 mb-8">
                <Music className="w-8 h-8 text-gold-500" />
                <h2 className="font-playfair text-3xl font-bold text-charcoal">
                  {t('Albums', 'Álbumes')} <span className="text-gold-500">({albums.length})</span>
                </h2>
              </div>
              {albums.length === 0 ? (
                <p className="font-lato text-gray-500 text-center py-12">
                  {t('No albums available yet.', 'No hay álbumes disponibles aún.')}
                </p>
              ) : (
                <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-3 gap-8">
                  {albums.map((album) => (
                    <MusicCard key={album.id} item={album} />
                  ))}
                </div>
              )}
            </div>
          </section>

          {/* Singles Section */}
          <section className="py-16 bg-gray-50" data-testid="singles-section">
            <div className="container mx-auto px-4 md:px-8">
              <div className="flex items-center gap-4 mb-8">
                <Disc className="w-8 h-8 text-gold-500" />
                <h2 className="font-playfair text-3xl font-bold text-charcoal">
                  {t('Singles', 'Sencillos')} <span className="text-gold-500">({singles.length})</span>
                </h2>
              </div>
              {singles.length === 0 ? (
                <p className="font-lato text-gray-500 text-center py-12">
                  {t('No singles available yet.', 'No hay sencillos disponibles aún.')}
                </p>
              ) : (
                <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-4 gap-6">
                  {singles.map((single) => (
                    <MusicCard key={single.id} item={single} />
                  ))}
                </div>
              )}
            </div>
          </section>

          {/* Collections Section */}
          {collections.length > 0 && (
            <section className="py-16" data-testid="collections-section">
              <div className="container mx-auto px-4 md:px-8">
                <div className="flex items-center gap-4 mb-8">
                  <Package className="w-8 h-8 text-gold-500" />
                  <h2 className="font-playfair text-3xl font-bold text-charcoal">
                    {t('Collections', 'Colecciones')} <span className="text-gold-500">({collections.length})</span>
                  </h2>
                </div>
                <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-3 gap-8">
                  {collections.map((collection) => (
                    <MusicCard key={collection.id} item={collection} />
                  ))}
                </div>
              </div>
            </section>
          )}
        </>
      )}

      {/* Streaming Platforms */}
      <section className="py-16 bg-cream" data-testid="streaming-section">
        <div className="container mx-auto px-4 md:px-8 text-center">
          <h3 className="font-playfair text-2xl font-bold text-charcoal mb-8">
            {t('Also Available On', 'También Disponible En')}
          </h3>
          <div className="flex flex-wrap justify-center gap-8">
            <a 
              href="https://open.spotify.com/artist/4mVbIGHmeO5AcP5TrLk2V2"
              target="_blank"
              rel="noopener noreferrer"
              className="flex items-center gap-2 px-6 py-3 bg-white rounded-full shadow-md hover:shadow-lg transition-shadow"
            >
              <svg className="w-6 h-6" viewBox="0 0 24 24" fill="#1DB954">
                <path d="M12 0C5.4 0 0 5.4 0 12s5.4 12 12 12 12-5.4 12-12S18.66 0 12 0zm5.521 17.34c-.24.359-.66.48-1.021.24-2.82-1.74-6.36-2.101-10.561-1.141-.418.122-.779-.179-.899-.539-.12-.421.18-.78.54-.9 4.56-1.021 8.52-.6 11.64 1.32.42.18.479.659.301 1.02zm1.44-3.3c-.301.42-.841.6-1.262.3-3.239-1.98-8.159-2.58-11.939-1.38-.479.12-1.02-.12-1.14-.6-.12-.48.12-1.021.6-1.141C9.6 9.9 15 10.561 18.72 12.84c.361.181.54.78.241 1.2zm.12-3.36C15.24 8.4 8.82 8.16 5.16 9.301c-.6.179-1.2-.181-1.38-.721-.18-.601.18-1.2.72-1.381 4.26-1.26 11.28-1.02 15.721 1.621.539.3.719 1.02.419 1.56-.299.421-1.02.599-1.559.3z"/>
              </svg>
              <span className="font-lato font-semibold text-charcoal">Spotify</span>
            </a>
            <a 
              href="https://music.apple.com/us/artist/sarah-capeles/858107097"
              target="_blank"
              rel="noopener noreferrer"
              className="flex items-center gap-2 px-6 py-3 bg-white rounded-full shadow-md hover:shadow-lg transition-shadow"
            >
              <svg className="w-6 h-6" viewBox="0 0 24 24" fill="#FA243C">
                <path d="M23.997 6.124c0-.738-.065-1.47-.24-2.19-.317-1.31-1.062-2.31-2.18-3.043C21.003.517 20.373.285 19.7.164c-.517-.093-1.038-.135-1.564-.15-.04-.003-.083-.01-.124-.013H5.988c-.152.01-.303.017-.455.026C4.786.07 4.043.15 3.34.428 2.004.958 1.04 1.88.475 3.208c-.192.448-.292.925-.363 1.408-.056.392-.088.785-.1 1.18 0 .032-.007.062-.01.093v12.223c.01.14.017.283.027.424.05.815.154 1.624.497 2.373.65 1.42 1.738 2.353 3.234 2.802.42.127.856.187 1.293.228.555.053 1.11.06 1.667.06h11.03c.525 0 1.048-.034 1.57-.1.823-.106 1.597-.35 2.296-.81.84-.553 1.472-1.287 1.88-2.208.186-.42.293-.87.37-1.324.113-.675.138-1.358.137-2.04-.002-3.8 0-7.595-.003-11.393zm-6.423 3.99v5.712c0 .417-.058.827-.244 1.206-.29.59-.76.962-1.388 1.14-.35.1-.706.157-1.07.173-.95.042-1.785-.56-2.075-1.46-.3-.928.1-1.94 1.003-2.384.344-.17.717-.26 1.1-.312.45-.063.9-.13 1.35-.197.193-.03.377-.1.55-.198.26-.15.37-.373.37-.69V8.93c0-.37-.088-.47-.46-.39l-3.046.73-1.942.468c-.013.003-.025.01-.04.015-.13.053-.2.143-.213.293-.01.103-.015.21-.015.314v7.237c0 .37-.046.732-.2 1.073-.3.666-.816 1.074-1.52 1.256-.347.09-.7.138-1.058.157-.9.047-1.716-.48-2.032-1.31-.347-.91.01-1.965.875-2.453.37-.21.775-.3 1.19-.362.37-.053.74-.107 1.11-.16.254-.04.5-.104.733-.215.267-.127.398-.343.413-.64.004-.072.007-.145.007-.218V6.664c0-.216.035-.426.14-.613.136-.24.34-.375.6-.44.166-.042.335-.077.505-.107L13.55 4.88l3.263-.78c.377-.09.755-.18 1.133-.266.18-.04.362-.068.547-.05.31.03.503.21.57.51.022.1.032.2.032.3.002 1.84.002 3.68.002 5.52z"/>
              </svg>
              <span className="font-lato font-semibold text-charcoal">Apple Music</span>
            </a>
            <a 
              href="https://www.youtube.com/sfrodelly"
              target="_blank"
              rel="noopener noreferrer"
              className="flex items-center gap-2 px-6 py-3 bg-white rounded-full shadow-md hover:shadow-lg transition-shadow"
            >
              <svg className="w-6 h-6" viewBox="0 0 24 24" fill="#FF0000">
                <path d="M23.498 6.186a3.016 3.016 0 0 0-2.122-2.136C19.505 3.545 12 3.545 12 3.545s-7.505 0-9.377.505A3.017 3.017 0 0 0 .502 6.186C0 8.07 0 12 0 12s0 3.93.502 5.814a3.016 3.016 0 0 0 2.122 2.136c1.871.505 9.376.505 9.376.505s7.505 0 9.377-.505a3.015 3.015 0 0 0 2.122-2.136C24 15.93 24 12 24 12s0-3.93-.502-5.814zM9.545 15.568V8.432L15.818 12l-6.273 3.568z"/>
              </svg>
              <span className="font-lato font-semibold text-charcoal">YouTube</span>
            </a>
          </div>
        </div>
      </section>
    </div>
  );
}
